import { isBlank, isEmpty, isNone, isPresent } from '@ember/utils';
import isEqual from 'lodash.isequal';
import convertOperator from 'cio/utils/operators/convert';
import { allOperators as conditionsOperators } from 'cio/utils/conditions-operators';
import { forEachSegmentable } from 'cio/utils/action-info';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import DeviceAttributeCondition from 'cio/app-models/conditions/device-attribute';
import EmailCondition from 'cio/app-models/conditions/email';
import EventCondition from 'cio/app-models/conditions/event';
import FormCondition from 'cio/app-models/conditions/form';
import PageviewCondition from 'cio/app-models/conditions/pageview';
import ScreenviewCondition from 'cio/app-models/conditions/screenview';
import ActionCondition from 'cio/app-models/conditions/action';
import RelationshipCondition from 'cio/app-models/conditions/relationship';
import { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';
import { ConditionType } from 'cio/utils/condition-types';

export function isComplete(condition) {
  let { type } = condition;

  if (
    [
      ConditionType.Attribute,
      ConditionType.EventAttribute,
      ConditionType.CollectionAttribute,
      ConditionType.DeviceAttribute,
      ConditionType.ObjectAttribute,
      ConditionType.RelationshipAttribute,
    ].includes(type)
  ) {
    return !(isEmpty(condition.field) || isEmpty(condition.operator));
  } else if (type === 'attribute_compare') {
    return !(
      isEmpty(condition.field) ||
      isEmpty(condition.operator) ||
      isEmpty(condition.value) ||
      isEmpty(condition.field_source) ||
      isEmpty(condition.value_source)
    );
  } else if (type === 'segments') {
    return !isEmpty(condition.segments);
  } else if (type === 'segments_negatable') {
    return !(isEmpty(condition.segments) || isEmpty(condition.negate));
  } else if (type === 'newsletter_recipients') {
    return !(isEmpty(condition.newsletter_id) || isEmpty(condition.negate));
  } else if (type === 'message') {
    return !(isEmpty(condition.event.name) || isEmpty(condition.event.type) || isEmpty(condition.inverse));
  } else if (type === 'event') {
    return !(isEmpty(condition.event.name) || isEmpty(condition.inverse));
  } else if (type === 'relationship') {
    return !(isEmpty(condition.name) || isEmpty(condition.inverse));
  } else if (type === 'foreign_event') {
    return !isEmpty(condition.event.name);
  } else if (type === 'collection_attribute') {
    return !(isEmpty(condition.field) || isEmpty(condition.operator));
  }
}

export function isDuplicate(condition, conditions) {
  let { type } = condition;

  if (
    [
      ConditionType.Attribute,
      ConditionType.EventAttribute,
      ConditionType.CollectionAttribute,
      ConditionType.DeviceAttribute,
      ConditionType.ObjectAttribute,
      ConditionType.RelationshipAttribute,
    ].includes(type)
  ) {
    return conditions.some(function (existingCondition) {
      if (isPresent(condition?.value2)) {
        return (
          existingCondition.type === type &&
          existingCondition.field === condition.field &&
          existingCondition.operator === condition.operator &&
          existingCondition.value === condition.value &&
          existingCondition?.value2 === condition.value2
        );
      }

      return (
        existingCondition.type === type &&
        existingCondition.field === condition.field &&
        existingCondition.operator === condition.operator &&
        existingCondition.value === condition.value
      );
    });
  }

  if (type === 'attribute_compare') {
    return conditions.any(function (existingCondition) {
      let isDuplicate =
        existingCondition.type === type &&
        existingCondition.field === condition.field &&
        existingCondition.operator === condition.operator &&
        existingCondition.value === condition.value &&
        existingCondition.field_source === condition.field_source &&
        existingCondition.value_source === condition.value_source;

      let convertedOp = convertOperator(condition.operator, { to: 'attribute' });
      let operator = conditionsOperators.findBy('value', convertedOp);

      if (operator.allowDuplicate && isDuplicate) {
        isDuplicate = existingCondition.value === condition.value;
      }

      return isDuplicate;
    });
  }

  if (type === 'segments') {
    return conditions.any(function (existingCondition) {
      return (
        existingCondition.type === 'segments_negatable' &&
        existingCondition.segments.length === condition.segments.length &&
        existingCondition.segments.every((segmentId) => condition.segments.any((value) => value === segmentId))
      );
    });
  }

  if (type === 'segments_negatable') {
    return conditions.any(function (existingCondition) {
      return (
        existingCondition.type === 'segments_negatable' &&
        existingCondition.negate === condition.negate &&
        existingCondition.segments.length === condition.segments.length &&
        existingCondition.segments.every((segmentId) => condition.segments.any((value) => value === segmentId))
      );
    });
  }

  if (type === 'newsletter_recipients') {
    return conditions
      .filter((c) => c.type === 'newsletter_recipients')
      .any(
        (existingCondition) =>
          existingCondition.newsletter_id === condition.newsletter_id && existingCondition.negate === condition.negate,
      );
  }

  if (type === 'message') {
    return conditions
      .filter((c) => c.type === 'message')
      .any((existingCondition) => {
        if (
          existingCondition.event.name !== condition.event.name ||
          existingCondition.event.type !== condition.event.type ||
          existingCondition.inverse !== condition.inverse
        ) {
          return false;
        }

        let shouldCompareFilters =
          Object.keys(existingCondition?.event?.filters ?? {}).length > 0 ||
          Object.keys(condition?.event?.filters ?? {}).length > 0;

        if (shouldCompareFilters && !isEqual(existingCondition.event.filters, condition.event.filters)) {
          return false;
        }

        // existing conditions can have isForeignEvent undefined because that is not stored on BE
        // isForeignEvent is created for use in FE for newly created action message conditions
        if (Boolean(existingCondition.isForeignEvent) !== Boolean(condition.isForeignEvent)) {
          return false;
        }

        return true;
      });
  }

  if (type === 'event') {
    return conditions
      .filter((c) => c.type === 'event')
      .any(
        (existingCondition) =>
          existingCondition.event.name === condition.event.name && existingCondition.inverse === condition.inverse,
      );
  }

  if (type === 'relationship') {
    return conditions
      .filter((c) => c.type === 'relationship')
      .any(
        (existingCondition) =>
          existingCondition.typeId === condition.typeId && existingCondition.inverse === condition.inverse,
      );
  }
}

export const CONDITION_TYPES = {
  segments: {
    name: ConditionType.Segments,
    icon: 'segment',
    addButtonLabel: 'Add segment condition',
  },
  segments_negatable: {
    name: ConditionType.SegmentsNegatable,
    icon: 'segment',
    addButtonLabel: 'Add segment condition',
  },
  attribute: {
    name: ConditionType.Attribute,
    icon: 'person',
    addButtonLabel: 'Add attribute condition',
  },
  object_attribute: {
    name: ConditionType.ObjectAttribute,
    icon: 'object',
    addButtonLabel: 'Add object attribute condition',
  },
  event: {
    name: ConditionType.Event,
    icon: 'event',
    addButtonLabel: 'Add event condition',
  },
  relationship: {
    name: ConditionType.Relationship,
    icon: 'object',
    addButtonLabel: 'Add relationship condition',
  },
  form: {
    name: ConditionType.Form,
    icon: 'form',
    addButtonLabel: 'Add form condition',
  },
  newsletter_recipients: {
    name: ConditionType.NewsletterRecipients,
    icon: 'newsletter',
    addButtonLabel: 'Add newsletter condition',
  },
  event_attribute: {
    name: ConditionType.EventAttribute,
    icon: 'event',
    addButtonLabel: 'Add event attribute condition',
  },
  message: {
    name: ConditionType.Message,
    icon: 'email',
    addButtonLabel: 'Add message condition',
  },
  collection_attribute: {
    name: ConditionType.CollectionAttribute,
    icon: 'person',
    addButtonLabel: 'Add query condition',
  },
  device_attribute: {
    name: ConditionType.DeviceAttribute,
    icon: 'mobile',
    addButtonLabel: 'Add device condition',
  },
  relationship_attribute: {
    name: ConditionType.RelationshipAttribute,
    icon: 'object',
    addButtonLabel: 'Add relationship attribute condition',
  },
};

export const CONDITION_CLASSES = {
  attribute: AttributeCondition,
  device_attribute: DeviceAttributeCondition,
  email: EmailCondition,
  event: EventCondition,
  foreign_event: EventCondition,
  form: FormCondition,
  pageview: PageviewCondition,
  screenview: ScreenviewCondition,
  relationship: RelationshipCondition,
};

forEachSegmentable(function (action) {
  CONDITION_CLASSES[action] = ActionCondition;
});

export function createCondition(type, options) {
  let condition = CONDITION_CLASSES[type];

  if (isNone(condition)) {
    return;
  }

  return condition.create({
    type,
    defaultToWildcard: options?.defaultToWildcard,
    shouldValidate: options?.shouldValidate ?? false,
    typeId: options?.typeId ?? '1',
  });
}

export function isEmptyAttributeCondition(condition) {
  let operatorValue = convertOperator(condition.operator, { to: 'attribute' });
  let operator = conditionsOperators.findBy('value', operatorValue);

  return (
    isEmpty(condition.field) ||
    isEmpty(condition.operator) ||
    (isBlank(condition.value) && operator.allowBlankPredicate === false)
  );
}

export function isEmptyCondition(condition) {
  if (isNone(condition)) {
    return true;
  }

  let { type } = condition;
  let empty = false;
  if (
    [
      ConditionType.Attribute,
      ConditionType.EventAttribute,
      ConditionType.CollectionAttribute,
      ConditionType.DeviceAttribute,
      ConditionType.ObjectAttribute,
      ObjectAttributeSource.ObjectAttribute,
      ObjectAttributeSource.RelationshipAttribute,
    ].includes(type)
  ) {
    empty = isEmptyAttributeCondition(condition);
  } else if (type === 'attribute_compare') {
    empty = isEmptyAttributeCondition(condition) || isEmpty(condition.field_source) || isEmpty(condition.value_source);
  } else if (type === ConditionType.Segments) {
    empty = isEmpty(condition.segments);
  } else if (type === ConditionType.SegmentsNegatable) {
    empty = isEmpty(condition.segments) || isEmpty(condition.negate);
  } else if (type === ConditionType.NewsletterRecipients) {
    empty = isEmpty(condition.newsletter_id) || isEmpty(condition.negate);
  }

  return empty;
}

export function getConditionCampaignFirstStarted(condition, filters) {
  let campaignFirstStarted = undefined;
  let action = 'email' in condition ? 'email' : 'action';
  // e.g "any_`action`-campaign_5" -> "5"
  let campaignId = condition[action]?.match(/-campaign_(\d+)$/)?.[1];

  if (campaignId != null) {
    let campaignFilter = filters.campaigns.findBy('value', campaignId);
    if (campaignFilter != null) {
      campaignFirstStarted = campaignFilter.first_started;
    }
  }

  return campaignFirstStarted;
}

// TODO: After conversion to typescript, this file should incorporate condition-types.ts
